/**
 * A JOURNEY, as a closed-form function of elapsed time.
 *
 * Travelling to a chosen place is a journey with a duration, never a teleport, and the
 * whole journey is a pure function of (start, destination, speed, elapsed). Nothing
 * integrates and nothing is stepped, so a journey computed in one evaluation and a
 * journey computed in ten thousand evaluations produce the identical result, bit for bit
 * - which is the same statement as "a player who closed the tab mid-journey arrives
 * exactly where a player who watched would have."
 *
 * -- THE CLAMP IS GONE, AND THAT IS THE WHOLE OF THIS ROUND'S CHANGE ---------
 *
 * Round 3's law was `clamp(d / cruise, minSeconds, ceilingSeconds)`, with the ceiling
 * documented as a promise about the world: "no journey between any two points of the sea
 * ever takes more than that, at any world size". Two things followed, and round 3's critic
 * MEASURED both (CL3-C-02):
 *
 *   THE FENCE COULD NOT FAIL. `duration <= ceiling` is the clamp's own postcondition. It
 *   passed at d = 9.007e15 leagues, an implied 2.566e14 m/s, and the test file next to it
 *   asserted `journeyDurationMs(1e6) === ceiling` - so the author knew.
 *
 *   THE READOUT SAID ONE THING. 370 of the chart's 380 ordered pairs pinned at the
 *   ceiling: 97.37%, six distinct ETA values in the whole chart, p10 = p50 = p90 = 180 s.
 *   The chart's ETA column was a constant that a player would read as information.
 *
 * The cause was not the ceiling, the pace or the sea alone: cruise x ceiling covered 99
 * leagues against a median inter-place distance of 519. A three-minute crossing, a cozy
 * boat and a 6.9 km sea are mutually inconsistent, and the clamp was silently reconciling
 * them by running the boat at 61.5 knots.
 *
 * SO THE LAW IS NOW A SPEED. Duration is distance over the passage speed, with one floor
 * so that nothing is instant. It is strictly increasing in distance across the whole real
 * range, so every pair on the chart gets its own ETA and the readout carries information
 * again. The wayfinding promise survives as a MEASUREMENT over the shipped chart - the
 * longest passage, against PASSAGE_CEILING_MINUTES - which is a fence with an independent
 * source and a negative control, both in journey.test.ts.
 *
 * -- WHAT OTHER LANES SHOULD READ --------------------------------------------
 *
 * `journeySpeedLeaguesPerSecond(distance, law)` is still the function to read per journey,
 * and it now returns the passage speed for everything above the floor. living-sea sizes a
 * wake from it, world-life a sail cycle, world-render a camera lead. The one number they
 * may take without a distance is `law.passageLeaguesPerSecond`, and `journeySpeedProfile`
 * publishes what fraction of real journeys that number describes exactly - MEASURED at
 * 100% of the pairs above the floor, against round 3's 2.63%.
 */

import { JOURNEY_MIN_SECONDS, SAIL_PASSAGE_LEAGUES_PER_SECOND } from './constants';
import type { Leagues, Millis, Vec2 } from './units';
import { clamp01, lerpVec2, smoothstep } from './units';

export interface JourneyLaw {
  /** Leagues per second under orders. THE boat's speed, not the middle of a range. */
  readonly passageLeaguesPerSecond: number;
  /** Seconds below which a journey is stretched, so nothing reads as a teleport. */
  readonly minSeconds: number;
}

export const DEFAULT_JOURNEY_LAW: JourneyLaw = {
  passageLeaguesPerSecond: SAIL_PASSAGE_LEAGUES_PER_SECOND,
  minSeconds: JOURNEY_MIN_SECONDS,
};

/**
 * Duration of a journey of this length, in WHOLE milliseconds. Always finite and positive.
 *
 * The rounding is load-bearing, not tidiness. A duration with a fractional millisecond
 * cannot be added exactly to a wall-clock timestamp: at a real epoch near 1.7e12 a double
 * has about a quarter of a microsecond of resolution left, so `issuedAt + durationMs`
 * comes back a hair SHORT of the arrival and the boat is still 'returning' at the instant
 * its own published ETA said it had arrived. exits.test.ts caught that while pressing
 * every exit in the reachable state space, which is exactly the kind of one-tick-late
 * arrival a hand-written test walks past.
 *
 * Whole milliseconds are exact integers at any timestamp this game will ever see, so
 * arrival is arrival.
 */
export function journeyDurationMs(distanceLeagues: Leagues, law: JourneyLaw = DEFAULT_JOURNEY_LAW): Millis {
  const d = Number.isFinite(distanceLeagues) ? Math.max(0, distanceLeagues) : 0;
  const speed = law.passageLeaguesPerSecond > 0 ? law.passageLeaguesPerSecond : 1;
  const seconds = Math.max(law.minSeconds, d / speed);
  return Math.round(seconds * 1000);
}

/**
 * THE SPEED OF THIS JOURNEY. Exactly `distance / duration`, so it agrees with
 * `journeyDurationMs` by construction rather than by convention, including over the floor
 * where a short hop is deliberately sailed slowly. A zero-length journey reports 0.
 */
export function journeySpeedLeaguesPerSecond(
  distanceLeagues: Leagues,
  law: JourneyLaw = DEFAULT_JOURNEY_LAW,
): number {
  const d = Number.isFinite(distanceLeagues) ? Math.max(0, distanceLeagues) : 0;
  if (d === 0) return 0;
  return d / (journeyDurationMs(d, law) / 1000);
}

/** Leagues below which the floor stretches a journey, so it runs slower than passage. */
export function floorLeagues(law: JourneyLaw = DEFAULT_JOURNEY_LAW): Leagues {
  return law.passageLeaguesPerSecond * law.minSeconds;
}

export interface JourneySpeedBounds {
  /** Slowest non-zero journey: the shortest hop that exists, stretched over the floor. */
  readonly slowest: number;
  /** The passage speed, which now describes every journey above the floor exactly. */
  readonly passage: number;
  /**
   * Fastest journey in this sea. It IS the passage speed: with the ceiling gone there is
   * nothing above it. Kept as a named field so a consuming lane budgeting for a range does
   * not have to know that the range collapsed.
   */
  readonly fastest: number;
  /** Shortest hop the passage speed still describes exactly. Leagues. */
  readonly floorLeagues: Leagues;
}

/**
 * The speed budget for a sea whose longest journey is `longestLeagues`. MEASURED from the
 * law rather than asserted, so a lane sizing a wake or a sail cycle can see the whole
 * range it has to cover instead of one number from the middle of it.
 */
export function journeySpeedBounds(
  longestLeagues: Leagues,
  law: JourneyLaw = DEFAULT_JOURNEY_LAW,
): JourneySpeedBounds {
  const floor = floorLeagues(law);
  const longest = Number.isFinite(longestLeagues) ? Math.max(0, longestLeagues) : 0;
  return {
    // The slowest journey is the shortest one that still exists; a hop of one hundredth
    // of a league is the honest floor, and it is the min-seconds stretch that sets it.
    slowest: journeySpeedLeaguesPerSecond(Math.min(0.01, longest || 0.01), law),
    passage: law.passageLeaguesPerSecond,
    fastest: journeySpeedLeaguesPerSecond(Math.max(longest, floor), law),
    floorLeagues: floor,
  };
}

export interface JourneySpeedProfile {
  /** Ordered pairs measured. */
  readonly pairs: number;
  /** Fraction of them the passage speed describes exactly. */
  readonly passageFraction: number;
  /** Fraction stretched over the floor, i.e. running slower than passage. */
  readonly floorFraction: number;
  readonly longestLeagues: Leagues;
  readonly longestSeconds: number;
  /** How many different ETAs the chart shows. Six, out of 380, was the defect. */
  readonly distinctEtaSeconds: number;
  readonly bounds: JourneySpeedBounds;
}

/**
 * THE MEASURED SPEED PROFILE of a set of places. Published as an API, not left in a test,
 * because living-sea sizes the wake from a speed, world-life times the sail cycle from
 * one, and world-render sizes the camera lead from one - and round 1 gave all three a
 * single constant that described a minority of journeys.
 *
 * `distinctEtaSeconds` is here because it is the quantity that showed the old law was
 * broken and the only one that shows the new law is not. It is a property of the CHART,
 * not of the law, which is what makes the assertion on it in journey.test.ts a fence
 * rather than a restatement.
 */
export function journeySpeedProfile(
  positions: readonly Vec2[],
  law: JourneyLaw = DEFAULT_JOURNEY_LAW,
): JourneySpeedProfile {
  const floor = floorLeagues(law);
  const etas = new Set<number>();
  let pairs = 0;
  let atPassage = 0;
  let floored = 0;
  let longest = 0;

  for (const a of positions) {
    for (const b of positions) {
      if (a === b) continue;
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      pairs += 1;
      longest = Math.max(longest, d);
      etas.add(journeyDurationMs(d, law));
      if (d < floor) floored += 1;
      else atPassage += 1;
    }
  }

  return {
    pairs,
    passageFraction: pairs === 0 ? 0 : atPassage / pairs,
    floorFraction: pairs === 0 ? 0 : floored / pairs,
    longestLeagues: longest,
    longestSeconds: journeyDurationMs(longest, law) / 1000,
    distinctEtaSeconds: etas.size,
    bounds: journeySpeedBounds(longest, law),
  };
}

/** Fraction of a journey completed. Clamped, so clock skew cannot push it out of [0,1]. */
export function journeyFraction(elapsedMs: Millis, durationMs: Millis): number {
  if (!(durationMs > 0)) return 1;
  if (!Number.isFinite(elapsedMs)) return 1;
  return clamp01(elapsedMs / durationMs);
}

/**
 * Position along a journey. Eased, so a journey reads as leaving and arriving rather than
 * as a constant-speed slide, and closed form, so it never drifts.
 */
export function journeyPosition(from: Vec2, to: Vec2, elapsedMs: Millis, durationMs: Millis): Vec2 {
  return lerpVec2(from, to, smoothstep(journeyFraction(elapsedMs, durationMs)));
}
